import '@mantine/charts/styles.css'
import { createTheme, MantineProvider } from '@mantine/core'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import { modals } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import '@mantine/notifications/styles.css'
import { PostHogProvider } from '@posthog/react'
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import axios from 'axios'
import Loader from 'common/Loader'
import ErrorPage from 'components/layout/ErrorPage'
import NotFoundPage from 'components/layout/NotFoundPage'
import dayjs from 'dayjs'
import 'dayjs/locale/pl'
import 'mantine-react-table/styles.css'
import posthog, { PostHogConfig } from 'posthog-js'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { toastError, toastSuccess } from './notifications'
import { routeTree } from './routeTree.gen'

dayjs.locale('pl')

axios.defaults.baseURL = '/api'
axios.defaults.withCredentials = true

const postHogOptions: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  disable_compression: true,
  defaults: '2025-05-24',
  cross_subdomain_cookie: false,
} as const

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (e, _, __, mutation) => {
      const meta = mutation.meta
      if (meta?.isErrorToasted === false) return
      toastError(e.response?.data.message || 'Nie udało się wykonać tej operacji. Odśwież stronę i spróbuj ponownie.')
    },
    onSuccess: (_, __, ___, mutation) => {
      const meta = mutation.meta
      if (!meta) return
      if (meta.closeModal) modals.closeAll()
      if (meta.successMessage) {
        toastSuccess(meta.successMessage as string)
      }
    },
  }),
})

// Set up a Router instance
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    queryClient,
    account: undefined!,
  },
  defaultPreloadStaleTime: 0,
  defaultOnCatch(error, errorInfo) {
    posthog.captureException(error, errorInfo)
  },
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: ({ error }) => <ErrorPage error={error} />,
  defaultPendingComponent: Loader,
})

const theme = createTheme({
  primaryColor: 'brand',
  colors: {
    brand: [
      '#ffecf8',
      '#f7dae8',
      '#e7b5cd',
      '#d88cb1',
      '#c96696',
      '#c3548a',
      '#c04883',
      '#aa3970',
      '#983164',
      '#872557',
    ],
  },
})

const PostHogProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  // wrap with PostHogProvider only if apiKey is provided
  const postHogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
  if (!postHogKey) {
    return <>{children}</>
  }
  return (
    <PostHogProvider apiKey={postHogKey} options={postHogOptions}>
      {children}
    </PostHogProvider>
  )
}

const rootElement = document.getElementById('app')!

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <PostHogProviderWrapper>
        <QueryClientProvider client={queryClient}>
          <MantineProvider theme={theme}>
            <Notifications />
            <RouterProvider router={router} />
          </MantineProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </PostHogProviderWrapper>
    </StrictMode>
  )
}
